/*
 * Copyright (c) 2026 Gabriele Bonora. Tutti i diritti riservati.
 *
 * Software proprietario. Nessuna licenza d'uso, copia, modifica o distribuzione
 * e' concessa in assenza di autorizzazione scritta del titolare dei diritti.
 * Vedere il file LICENSE nella radice del progetto.
 */
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { FileText, CheckCircle, Clock, Calendar, Plus, ChevronRight, Wrench, Star } from 'lucide-react';
import ClienteLayout from '../components/ClienteLayout';
import { useAuth } from '../context/AuthContext';
import { dashboardAPI } from '../api/dashboard';
import { prodottiAPI } from '../api/prodotti';
import { ctaManutenzione } from '../utils/manutenzione';
import { fmtDataOra } from '../utils/date';
import { useViewport } from '../hooks/useViewport';

const ACCENT = '#3B82F6';
const STATO_LABELS = { in_attesa: 'In attesa', confermato: 'Confermato', in_corso: 'In corso', completato: 'Completato', annullato: 'Annullato' };
const PRIO = { urgente: { bg: '#FEE2E2', color: '#991B1B', label: 'Urgente' }, alta: { bg: '#FED7AA', color: '#9A3412', label: 'Alta' }, normale: { bg: '#E0E7FF', color: '#3730A3', label: 'Normale' }, bassa: { bg: '#F3F4F6', color: '#4B5563', label: 'Bassa' } };

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { isMobile } = useViewport();
  const { data, isLoading } = useQuery({ queryKey: ['dashboard-cliente'], queryFn: () => dashboardAPI.cliente() });
  const { data: scadenze } = useQuery({ queryKey: ['scadenze-cliente'], queryFn: () => prodottiAPI.scadenze() });

  if (isLoading) return <ClienteLayout title="Home"><div style={styles.loading}>Caricamento...</div></ClienteLayout>;

  return (
    <ClienteLayout>
      <div style={styles.welcome}>
        <h1 style={{ margin: 0 }}>Ciao {user?.nome} 👋</h1>
        <p style={{ color: '#6B7280', margin: '0.3rem 0 0' }}>Ecco la situazione dei tuoi interventi.</p>
      </div>

      <div style={{ ...styles.kpiRow, ...(isMobile ? styles.kpiRowMobile : {}) }}>
        <div style={{ ...styles.kpiCard, ...(isMobile ? styles.kpiCardMobile : {}) }}>
          <div style={{ ...styles.kpiIcon, backgroundColor: ACCENT + '20', color: ACCENT }}><FileText size={20} /></div>
          <div><div style={{ ...styles.kpiVal, ...(isMobile ? styles.kpiValMobile : {}) }}>{data.totali.totale}</div><div style={styles.kpiLabel}>Totali</div></div>
        </div>
        <div style={{ ...styles.kpiCard, ...(isMobile ? styles.kpiCardMobile : {}) }}>
          <div style={{ ...styles.kpiIcon, backgroundColor: '#FEF3C7', color: '#92400E' }}><Clock size={20} /></div>
          <div><div style={{ ...styles.kpiVal, ...(isMobile ? styles.kpiValMobile : {}) }}>{data.totali.attivi}</div><div style={styles.kpiLabel}>In corso</div></div>
        </div>
        <div style={{ ...styles.kpiCard, ...(isMobile ? styles.kpiCardMobile : {}) }}>
          <div style={{ ...styles.kpiIcon, backgroundColor: '#D1FAE5', color: '#065F46' }}><CheckCircle size={20} /></div>
          <div><div style={{ ...styles.kpiVal, ...(isMobile ? styles.kpiValMobile : {}) }}>{data.totali.completati}</div><div style={styles.kpiLabel}>Completati</div></div>
        </div>
      </div>

      <button onClick={() => navigate('/nuovo')} style={styles.ctaBtn}>
        <Plus size={18} /> Richiedi un nuovo intervento
      </button>

{scadenze && scadenze.in_scadenza.length > 0 && (
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}><Wrench size={18} /> Manutenzioni da pianificare</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
            {scadenze.in_scadenza.slice(0, 4).map((s, idx) => {
              const col = s.colore === 'scaduta' ? '#DC2626' : s.colore === 'rosso' ? '#EF4444' : '#F59E0B';
              return (
                <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.85rem', backgroundColor: '#F9FAFB', borderRadius: '10px', cursor: 'pointer', flexWrap: isMobile ? 'wrap' : 'nowrap' }} onClick={() => navigate(`/prodotti/${s.prodotto_id}`)}>
                  <div style={{ width: '8px', height: '40px', borderRadius: '4px', backgroundColor: col, flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: isMobile ? '60%' : 'auto' }}>
                    <div style={{ fontWeight: 600 }}>{s.manutenzione}</div>
                    <div style={{ fontSize: '0.82rem', color: '#6B7280' }}>{s.prodotto} {s.marca} · {s.ubicazione || ''}</div>
                  </div>
                  <div style={{ textAlign: isMobile ? 'left' : 'right', display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap', width: isMobile ? '100%' : 'auto', paddingLeft: isMobile ? 'calc(8px + 0.75rem)' : 0 }}>
                    <div style={{ fontWeight: 700, color: col, fontSize: '0.85rem' }}>
                      {s.colore === 'scaduta' ? 'Scaduta' : `${s.giorni_rimanenti}gg`}
                    </div>
                    {(() => {
                      const cta = ctaManutenzione(s);
                      return (
                        <button onClick={(e) => {
                                  e.stopPropagation();
                                  if (cta.vaiAIntervento) navigate(`/interventi/${cta.vaiAIntervento}`);
                                  else navigate('/nuovo', { state: { precompilato: cta.precompilato } });
                                }}
                                style={{ padding: '0.45rem 0.8rem', backgroundColor: cta.colore, color: 'white', border: 'none', borderRadius: '7px', cursor: 'pointer', fontWeight: 600, fontSize: '0.76rem', whiteSpace: 'nowrap' }}>
                          {cta.label}
                        </button>
                      );
                    })()}
                  </div>
                </div>
              );
            })}
          </div>
          <button onClick={() => navigate('/prodotti')} style={{ ...styles.linkBtn, marginTop: '0.85rem' }}>Vedi tutti i prodotti →</button>
        </div>
      )}

      {data.prossimo_appuntamento && (
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}><Calendar size={18} /> Prossimo appuntamento</h3>
          <div style={styles.apptCard} onClick={() => navigate(`/interventi/${data.prossimo_appuntamento.id}`)}>
            <div>
              <div style={{ fontWeight: 600 }}>{data.prossimo_appuntamento.titolo}</div>
              <code style={styles.code}>{data.prossimo_appuntamento.codice}</code>
              <div style={{ color: ACCENT, fontWeight: 600, marginTop: '0.4rem' }}>{fmtDataOra(data.prossimo_appuntamento.data_appuntamento)}</div>
            </div>
            <ChevronRight size={20} color="#9CA3AF" />
          </div>
        </div>
      )}

      <div style={styles.section}>
        <div style={styles.sectionHeader}>
          <h3 style={styles.sectionTitle}><FileText size={18} /> Interventi recenti</h3>
          <button onClick={() => navigate('/interventi')} style={styles.linkBtn}>Vedi tutti →</button>
        </div>
        {data.ultimi_interventi.length === 0 ? (
          <div style={styles.empty}>
            <p>Non hai ancora richiesto interventi.</p>
            <button onClick={() => navigate('/nuovo')} style={styles.ctaBtnSm}><Plus size={16} /> Richiedi il primo</button>
          </div>
        ) : (
          <div style={styles.list}>
            {data.ultimi_interventi.map((i) => {
              const p = PRIO[i.priorita] || PRIO.normale;
              return (
                <div key={i.id} style={styles.card} onClick={() => navigate(`/interventi/${i.id}`)}>
                  <div style={{ flex: 1 }}>
                    <div style={styles.cardHead}>
                      <span style={{ ...styles.statoBadge, backgroundColor: i.stato_colore + '22', color: i.stato_colore }}>{STATO_LABELS[i.stato]}</span>
                      <span style={{ ...styles.prioBadge, backgroundColor: p.bg, color: p.color }}>{p.label}</span>
                      <code style={styles.code}>{i.codice}</code>
                    </div>
                    <div style={{ fontWeight: 600, marginTop: '0.35rem' }}>{i.titolo}</div>
                    {i.data_appuntamento && <div style={styles.cardMeta}><Calendar size={13} /> {fmtDataOra(i.data_appuntamento)}</div>}
                  </div>
                  <ChevronRight size={18} color="#9CA3AF" />
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Discreto e in fondo: chi vuole dire la sua lo trova, chi e' venuto per
          altro non viene interrotto. Niente popup. */}
      <button onClick={() => navigate('/dicci-la-tua')} style={styles.dicciCard}>
        <Star size={20} color="#F59E0B" fill="#FEF3C7" />
        <span style={{ flex: 1, textAlign: 'left' }}>
          <strong>Ti piace l'app?</strong>
          <span style={{ display: 'block', fontSize: '0.82rem', color: '#6B7280' }}>Dicci cosa vorresti e cosa miglioreresti</span>
        </span>
        <ChevronRight size={18} color="#9CA3AF" />
      </button>
    </ClienteLayout>
  );
}

const styles = {
  dicciCard: { display: 'flex', alignItems: 'center', gap: '0.75rem', width: '100%', marginTop: '1.25rem', padding: '0.9rem 1rem', backgroundColor: 'white', border: '1px solid #E5E7EB', borderRadius: '12px', cursor: 'pointer', fontFamily: 'inherit', fontSize: '0.95rem', color: '#111827' },
  loading: { padding: '3rem', textAlign: 'center', color: '#6B7280' },
  welcome: { marginBottom: '1.5rem' },
  kpiRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '0.85rem', marginBottom: '1.25rem' },
  kpiRowMobile: { gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.5rem' },
  kpiCard: { display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '1.1rem', backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' },
  kpiCardMobile: { flexDirection: 'column', gap: '0.35rem', padding: '0.75rem 0.4rem', textAlign: 'center' },
  kpiIcon: { width: '42px', height: '42px', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  kpiVal: { fontSize: '1.5rem', fontWeight: 700 },
  kpiValMobile: { fontSize: '1.2rem' },
  kpiLabel: { fontSize: '0.8rem', color: '#6B7280' },
  ctaBtn: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', width: '100%', padding: '0.9rem', backgroundColor: ACCENT, color: 'white', border: 'none', borderRadius: '12px', cursor: 'pointer', fontWeight: 600, fontSize: '1rem', marginBottom: '1.5rem' },
  ctaBtnSm: { display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '0.55rem 1rem', backgroundColor: ACCENT, color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, marginTop: '0.75rem' },
  section: { backgroundColor: 'white', padding: '1.25rem', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', marginBottom: '1.25rem' },
  sectionHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' },
  sectionTitle: { margin: 0, fontSize: '1.05rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '0.4rem' },
  linkBtn: { background: 'none', border: 'none', color: ACCENT, fontWeight: 600, cursor: 'pointer', fontSize: '0.85rem' },
  apptCard: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem', backgroundColor: '#EFF6FF', borderRadius: '10px', cursor: 'pointer' },
  list: { display: 'flex', flexDirection: 'column', gap: '0.6rem' },
  card: { display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.9rem 1rem', backgroundColor: '#F9FAFB', borderRadius: '10px', cursor: 'pointer' },
  cardHead: { display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' },
  statoBadge: { padding: '0.2rem 0.6rem', borderRadius: '6px', fontSize: '0.75rem', fontWeight: 700 },
  prioBadge: { padding: '0.18rem 0.5rem', borderRadius: '6px', fontSize: '0.72rem', fontWeight: 600 },
  code: { backgroundColor: '#E5E7EB', padding: '0.12rem 0.4rem', borderRadius: '4px', fontSize: '0.76rem', fontFamily: 'ui-monospace, monospace' },
  cardMeta: { display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.82rem', color: '#6B7280', marginTop: '0.35rem' },
  empty: { padding: '2rem', textAlign: 'center', color: '#6B7280' },
};
